import {
    PieChart,
    Pie,
    Cell,
    ResponsiveContainer,
    Tooltip,
} from "recharts";

const DEFAULT_COLORS = [
    "#0c5b67",
    "#30808c",
    "#7ab2ba",
    "#a8d0d6",
    "#d4a373",
    "#ccd5ae",
    "#e9edc9",
    "#faedcd",
];

const PieChartGraph = ({
    data,
    activeTab,
    nameKey = "name",
    valueKey = "value",
    colors = DEFAULT_COLORS,
    height = 320,
    percentLabels = true,
    showLabels = true,
    tooltipAsPercent = percentLabels,
    includeZero = false,
    innerRadius = 65,
    outerRadius = 95,
    legendAsPercent = false,
    centerLabel,
    centerSubLabel,
    tooltipValueFormatter = (value) => value,
}) => {
    const getDisplayData = () => {
        if (Array.isArray(data)) {
            return data;
        }

        if (data && activeTab && Array.isArray(data[activeTab])) {
            return data[activeTab];
        }

        if (data && typeof data === "object") {
            const firstKey = Object.keys(data)[0];

            if (firstKey && Array.isArray(data[firstKey])) {
                return data[firstKey];
            }
        }

        return [];
    };

    const displayData = getDisplayData()
        .map((item) => ({
            ...item,
            [nameKey]: item?.[nameKey] ?? "Sin nombre",
            [valueKey]: Number(item?.[valueKey] ?? 0),
        }))
        .filter((item) =>
            includeZero ? item[valueKey] >= 0 : item[valueKey] > 0
        );

    const pieTotal = displayData.reduce(
        (sum, item) => sum + item[valueKey],
        0
    );

    console.log("Datos para PieChart:", displayData);

    if (!displayData.length) {
        return (
            <div
                style={{
                    width: "100%",
                    minHeight: height,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "#777",
                    fontSize: "14px",
                }}
            >
                No hay datos disponibles
            </div>
        );
    }

    const renderCustomLabel = ({
        cx,
        cy,
        midAngle,
        outerRadius,
        percent,
        value,
        payload,
    }) => {
        const RADIAN = Math.PI / 180;

        // Aleja la etiqueta del gráfico
        const radius = outerRadius + 28;

        const x = cx + radius * Math.cos(-midAngle * RADIAN);
        const y = cy + radius * Math.sin(-midAngle * RADIAN);

        const name = payload?.[nameKey] ?? "Sin nombre";

        // Opcional: no mostrar labels de porciones muy pequeñas
        if (percentLabels && percent < 0.04) return null;

        const labelText = percentLabels
            ? `${name}: ${Number(value).toFixed(1)}%`
            : `${name}: ${value}`;

        return (
            <text
                x={x}
                y={y}
                fill="#333"
                textAnchor={x > cx ? "start" : "end"}
                dominantBaseline="central"
                fontSize={14}
                fontWeight={600}
            >
                {labelText}
            </text>
        );
    };

    return (
        <div
            style={{
                width: "100%",
                height,
                minHeight: height,
                display: "flex",
                flexDirection: "column",
            }}
        >
            <div style={{ flex: 1, minHeight: 0, position: "relative" }}>
                <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                    <Pie
                        data={displayData}
                        cx="50%"
                        cy="50%"
                        innerRadius={innerRadius}
                        outerRadius={outerRadius}
                        paddingAngle={5}
                        dataKey={valueKey}
                        nameKey={nameKey}
                        label={showLabels ? renderCustomLabel : false}
                        labelLine={false}
                    >
                        {displayData.map((entry, index) => (
                            <Cell
                                key={`cell-${index}`}
                                fill={colors[index % colors.length]}
                            />
                        ))}
                    </Pie>

                    <Tooltip
                        formatter={(value, name) => [
                            tooltipAsPercent
                                ? `${Number(value).toFixed(2)}%`
                                : tooltipValueFormatter(value),
                            name,
                        ]}
                        contentStyle={{
                            backgroundColor: "#fff",
                            border: "none",
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                            fontSize: "14px",
                        }}
                    />
                </PieChart>
                </ResponsiveContainer>

                {centerLabel && (
                    <div
                        style={{
                            position: "absolute",
                            top: "50%",
                            left: "50%",
                            transform: "translate(-50%, -50%)",
                            textAlign: "center",
                            pointerEvents: "none",
                        }}
                    >
                        <div
                            style={{
                                fontSize: "24px",
                                fontWeight: 700,
                                color: "#333",
                                lineHeight: 1.1,
                            }}
                        >
                            {centerLabel}
                        </div>
                        {centerSubLabel && (
                            <div
                                style={{
                                    fontSize: "12px",
                                    color: "#666",
                                    marginTop: "2px",
                                }}
                            >
                                {centerSubLabel}
                            </div>
                        )}
                    </div>
                )}
            </div>

            <div
                style={{
                    display: "flex",
                    flexWrap: "wrap",
                    gap: "16px",
                    justifyContent: "center",
                    paddingTop: "10px",
                }}
            >
                {displayData.map((entry, index) => (
                    <div
                        key={index}
                        style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                            fontSize: "13px",
                            color: "#666",
                        }}
                    >
                        <span
                            style={{
                                width: "12px",
                                height: "12px",
                                borderRadius: "50%",
                                background: colors[index % colors.length],
                                flexShrink: 0,
                                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
                            }}
                        ></span>
                        {legendAsPercent
                            ? `${entry.name}: ${pieTotal > 0 ? ((entry[valueKey] / pieTotal) * 100).toFixed(1) : 0}%`
                            : entry.name}
                    </div>
                ))}
            </div>
        </div>
    );
};

export default PieChartGraph;
